import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, CheckCircle, Star, Users, Clock, Shield } from 'lucide-react';
import { features } from '../mockData';
import { Calculator } from '../components/Calculator';

const iconMap = {
  'coins': Users,
  'calendar': Clock,
  'zap': ArrowRight,
  'check-circle': CheckCircle
};

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-amber-50 via-white to-emerald-50">
        <div className="absolute top-20 right-10 w-64 h-64 bg-amber-200 rounded-full blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-emerald-200 rounded-full blur-3xl opacity-30 animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-amber-500 to-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
                  ⚡ Aprobación Rápida
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-slate-900">Tu crédito</span>
                <br />
                <span className="bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
                  al instante
                </span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                Préstamos accesibles de persona a persona. Comienza con montos pequeños y pagos semanales cómodos. ¡Net está aquí para ayudarte!
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://wa.me/5213329698523?text=Hola,%20quiero%20información%20sobre%20un%20crédito" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-lg px-8 h-14 shadow-xl hover:shadow-2xl transition-all">
                    Solicita tu Crédito
                    <ArrowRight className="ml-2" />
                  </Button>
                </a>
                <Link to="/como-funciona">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-amber-500 text-slate-700 hover:text-amber-600 font-semibold text-lg px-8 h-14">
                    Cómo Funciona
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-emerald-300 border-2 border-white"></div>
                    ))}
                  </div>
                  <span className="text-sm text-slate-600 font-medium">+5,000 clientes satisfechos</span>
                </div>
              </div>
            </div>

            <div className="relative animate-in fade-in slide-in-from-right duration-700 delay-300">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
                  alt="Net el robot de PrestaNet" 
                  className="w-full max-w-lg mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-amber-300 to-emerald-300 rounded-3xl blur-2xl opacity-40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              ¿Por qué elegir <span className="bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">PrestaNet</span>?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Hacemos que obtener un crédito sea rápido, fácil y transparente
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon] || CheckCircle;
              return (
                <Card 
                  key={index} 
                  className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-amber-200 group"
                  style={{animationDelay: `${index * 100}ms`}}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 to-amber-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Calcula tu Préstamo
            </h2>
            <p className="text-xl text-slate-600">
              Descubre cuánto pagarás semanalmente
            </p>
          </div>
          <Calculator />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-amber-500 to-emerald-500">
        <div className="container mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
            <div className="space-y-2">
              <div className="text-5xl font-bold">6</div>
              <div className="text-lg opacity-90">Sucursales</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold">14</div>
              <div className="text-lg opacity-90">Semanas de Pago</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold">5K+</div>
              <div className="text-lg opacity-90">Clientes Felices</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold">24/7</div>
              <div className="text-lg opacity-90">Soporte</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
              alt="Net" 
              className="w-32 h-32 mx-auto"
            />
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Listo para tu crédito?
            </h2>
            <p className="text-xl text-slate-300">
              Net está esperando para ayudarte. Comienza ahora y obtén tu préstamo en minutos.
            </p>
            <a href="https://wa.me/5213329698523?text=Hola,%20quiero%20solicitar%20un%20crédito" target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-600 hover:to-emerald-600 text-white font-bold text-lg px-10 h-14 shadow-2xl">
                Hablar con Net
                <ArrowRight className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
